import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthProvider';
import { buildTimelineRows } from '../lib/timeline';

export function useOnTimeOrderEvents(orderId, fallbackTimeline = []) {
  const { claims, user } = useAuth();
  const orgId = claims?.orgId || '';
  const uid = user?.uid || '';
  const [timeline, setTimeline] = useState(fallbackTimeline);
  const fallbackKey = useMemo(() => JSON.stringify(fallbackTimeline || []), [fallbackTimeline]);

  useEffect(() => {
    let active = true;
    let unsub = null;

    if (!uid || !orgId || !orderId) {
      setTimeline(fallbackTimeline || []);
      return () => {};
    }

    try {
      unsub = onSnapshot(
        query(collection(db, 'orgs', orgId, 'ontimeOrders', orderId, 'events'), orderBy('createdAt', 'asc')),
        (snap) => {
          if (!active) return;
          try {
            if (snap.empty) {
              setTimeline(fallbackTimeline || []);
              return;
            }
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setTimeline(buildTimelineRows(rows, fallbackTimeline || []));
          } catch (error) {
            console.error('[OnTime] events snapshot error', error);
            setTimeline(fallbackTimeline || []);
          }
        },
        (error) => {
          console.error('[OnTime] events listener failed', error);
          if (!active) return;
          setTimeline(fallbackTimeline || []);
        }
      );
    } catch (error) {
      console.error('[OnTime] events listener setup failed', error);
      setTimeline(fallbackTimeline || []);
      unsub = null;
    }

    return () => {
      active = false;
      try {
        if (typeof unsub === 'function') unsub();
      } catch (error) {
        console.error('[OnTime] events unsubscribe failed', error);
      }
    };
  }, [orgId, uid, orderId, fallbackKey]);

  return timeline;
}
